/* GASSISA
=========================================================================================
* Descripción: Modal con el detalle de una nómina guardada (solo lectura)
* Muestra columnas dinámicas de bonos/deducciones basadas en el esquema del JSON.
* Historial de cambios:
* ---------------------------------------------------------------------------------------
*    Revisión   | Fecha      | Desarrollador                    | Resumen del cambio
* ---------------------------------------------------------------------------------------
*      1       | 07/08/2026 | Lic. José Luis Aragón Cervantes  | Creación
*      2       | 22/08/2026 | Lic. José Luis Aragón Cervantes  | Columnas dinámicas desde conceptos, lee campo conceptos del API
*      3       | 24/10/2026 | Lic. José Luis Aragón Cervantes  | Muestra el salario diario histórico por empleado cuando está disponible
* ---------------------------------------------------------------------------------------
*/

import { useMemo } from "react";
import {
    Box,
    Chip,
    CircularProgress,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";

import SizeSoftXModal from "../leyout/SizeSoftXModal";

const formatCurrency = (valor) =>
    new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(Number(valor) || 0);

const formatFecha = (fecha) => {
    const partes = String(fecha || "").split("-");
    if (partes.length !== 3) return fecha || "-";
    return `${partes[2]}/${partes[1]}/${partes[0]}`;
};

const parseJson = (value) => {
    try {
        const arr = JSON.parse(value || "[]");
        return Array.isArray(arr) ? arr : [];
    } catch {
        return [];
    }
};

const getConceptoValor = (conceptosArr, tipo, descripcion) => {
    const encontrado = (conceptosArr || []).find(
        (c) =>
            String(c?.tipo || "").toUpperCase() === String(tipo).toUpperCase() &&
            String(c?.descripcion || "").trim().toLowerCase() ===
                String(descripcion || "").trim().toLowerCase()
    );
    return Number(encontrado?.valor) || 0;
};

export default function ModalDetalleNomina({ open, onClose, cargando, detalle }) {
    const header = (detalle && detalle.header) || {};
    const filas = (detalle && detalle.detalle) || [];
    const total = filas.reduce((acc, f) => acc + (Number(f.total) || 0), 0);

    const esquema = useMemo(() => {
        for (const fila of filas) {
            const conceptos = parseJson(fila.conceptos || fila.bonosJson);
            if (conceptos.length > 0) {
                return conceptos.map((c) => ({
                    tipo: String(c.tipo || "").toUpperCase(),
                    descripcion: String(c.descripcion || "").trim(),
                    key: `${String(c.tipo || "").toUpperCase()}-${String(c.descripcion || "").trim()}`,
                }));
            }
        }
        return [];
    }, [filas]);

    return (
        <SizeSoftXModal
            open={open}
            onClose={onClose}
            onSuccess={onClose}
            success="Cerrar"
            hideCancel
            maxWidth="xl"
            title={`Detalle de nómina ${header.folio ? `#${header.folio}` : ""}`}
        >
            <Stack spacing={1.5}>
                <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                    <Chip label={`Folio: ${header.folio || "-"}`} size="small" variant="outlined" />
                    <Chip
                        label={`Periodo: ${formatFecha(header.fechainicio)} al ${formatFecha(header.fechafin)}`}
                        size="small"
                        variant="outlined"
                    />
                    <Chip
                        label={header.status || "EN PROCESO"}
                        size="small"
                        color={header.status === "TERMINADA" ? "success" : "default"}
                        variant="outlined"
                    />
                    <Chip label={`Total: ${formatCurrency(total)}`} size="small" color="primary" />
                    {esquema.length > 0 && (
                        <Chip label={`${esquema.length} concepto(s)`} size="small" variant="outlined" />
                    )}
                </Stack>

                {cargando ? (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                        <CircularProgress size={28} />
                    </Box>
                ) : (
                    <TableContainer sx={{ maxHeight: "55vh", overflow: "auto" }}>
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 700 }}>Usuario</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Empleado</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }} align="center">Días</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }} align="center">Horas</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }} align="center">Ret.</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }} align="right">Salario diario</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }} align="right">Nómina</TableCell>
                                    {esquema.map((c) => {
                                        const esBono = c.tipo === "BONO";
                                        return (
                                            <TableCell
                                                key={c.key}
                                                align="right"
                                                sx={{
                                                    fontWeight: 700,
                                                    whiteSpace: "nowrap",
                                                    backgroundColor: esBono ? "#e8f5e9" : "#fce4ec",
                                                    fontSize: "0.8rem",
                                                }}
                                            >
                                                <span style={{ color: esBono ? "#2e7d32" : "#c62828" }}>
                                                    {c.descripcion}
                                                </span>
                                            </TableCell>
                                        );
                                    })}
                                    <TableCell sx={{ fontWeight: 700 }} align="right">Comis.</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }} align="right">Total</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filas.map((fila, idx) => {
                                    const conceptos = parseJson(fila.conceptos || fila.bonosJson);
                                    return (
                                        <TableRow key={fila.id || fila.usuario || idx} hover>
                                            <TableCell sx={{ whiteSpace: "nowrap" }}>{fila.usuario}</TableCell>
                                            <TableCell>{fila.nombre}</TableCell>
                                            <TableCell align="center">{fila.dias}</TableCell>
                                            <TableCell align="center">{fila.horas}</TableCell>
                                            <TableCell align="center">{fila.retardos}</TableCell>
                                            <TableCell align="right">{fila.salariodiario == null ? "—" : formatCurrency(fila.salariodiario)}</TableCell>
                                            <TableCell align="right">{formatCurrency(fila.nominabase)}</TableCell>
                                            {esquema.map((c) => {
                                                const valor = getConceptoValor(conceptos, c.tipo, c.descripcion);
                                                const esBono = c.tipo === "BONO";
                                                return (
                                                    <TableCell
                                                        key={c.key}
                                                        align="right"
                                                        sx={{
                                                            backgroundColor: esBono ? "#f1f8e9" : "#fce4ec",
                                                        }}
                                                    >
                                                        {valor !== 0 ? formatCurrency(valor) : ""}
                                                    </TableCell>
                                                );
                                            })}
                                            <TableCell align="right">{formatCurrency(fila.comisiones)}</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 700 }}>
                                                {formatCurrency(fila.total)}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                            <TableBody>
                                <TableRow>
                                    <TableCell
                                        colSpan={7}
                                        align="right"
                                        sx={{ fontWeight: 700, borderTop: 2 }}
                                    >
                                        Total nómina
                                    </TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 700, borderTop: 2 }}>
                                        {formatCurrency(filas.reduce((a, f) => a + (Number(f.nominabase) || 0), 0))}
                                    </TableCell>
                                    {esquema.map((c) => {
                                        const totalConcepto = filas.reduce((a, f) => {
                                            return a + getConceptoValor(parseJson(f.conceptos || f.bonosJson), c.tipo, c.descripcion);
                                        }, 0);
                                        return (
                                            <TableCell
                                                key={c.key}
                                                align="right"
                                                sx={{ fontWeight: 700, borderTop: 2 }}
                                            >
                                                {totalConcepto !== 0 ? formatCurrency(totalConcepto) : ""}
                                            </TableCell>
                                        );
                                    })}
                                    <TableCell align="right" sx={{ fontWeight: 700, borderTop: 2 }}>
                                        {formatCurrency(filas.reduce((a, f) => a + (Number(f.comisiones) || 0), 0))}
                                    </TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 700, borderTop: 2 }}>
                                        {formatCurrency(total)}
                                    </TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Stack>
        </SizeSoftXModal>
    );
}
